import getHexColorFromNamed from './getHexColorFromNamed/index.js'
function clamp(min: number, value: number, max: number) {
  return Math.max(min, Math.min(value, max))
}

// The library we use for the color input uses this as a value for transparent
export const TRANSPARENT_COLOR_CODE: string = '00000000'

export function rgbToHex(components: [number, number, number] | readonly [number, number, number]): string {
  return `#${components.map((c) => c.toString(16).padStart(2, '0')).join('')}`
}

export function brightenColor(color: string, percent: number): string {
  if (color === 'transparent') return color
  const color_ = getHexColorFromNamed(color) ?? color
  const num = parseInt(color_.replace('#', ''), 16)
  const amt = Math.round(2.55 * percent)
  const R = clamp(0, ((num >> 16) & 255) + amt, 255)
  const G = clamp(0, ((num >> 8) & 255) + amt, 255)
  const B = clamp(0, (num & 255) + amt, 255)
  const components = [R, G, B].map((c) => (c >= 0 ? c : 0)) as [number, number, number]
  const stringified = rgbToHex(components)
  return stringified
}

export function darkenColor(color: string, percent: number): string {
  return brightenColor(color, -1 * percent)
}

export function addAlphaToColor(color: string, percent: number): string {
  if (color === 'transparent') return color
  const color_ = getHexColorFromNamed(color) || color
  const amt = Math.round(2.55 * percent)
  return `${color_}${amt.toString(16).padStart(2, '0')}`
}

export function getRGBFromHex(color: string): [number, number, number] {
  const color_ = getHexColorFromNamed(color) ?? color
  return [parseInt(color_.slice(1, 3), 16), parseInt(color_.slice(3, 5), 16), parseInt(color_.slice(5, 7), 16)]
}

export function getColorLuminance(color: string): number {
  if (color === 'transparent') return 0

  // Convert hex to RGB values between 0-1
  const [r, g, b] = getRGBFromHex(color).map((c) => c / 255) as [number, number, number]

  // Convert to sRGB
  const rsRGB = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
  const gsRGB = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
  const bsRGB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

  // Calculate luminance
  return 0.2126 * rsRGB + 0.7152 * gsRGB + 0.0722 * bsRGB
}

export function decodeColor(string?: unknown) {
  if (!string) return undefined
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  const str = typeof string !== 'string' ? String(string) : string

  // We need this case because our color picker uses this value for transparent
  if (string === TRANSPARENT_COLOR_CODE) return 'transparent'

  let m = str.match(/^([a-f\d]{3}|[a-f\d]{6})$/i)
  if (m && m[1]) {
    return `#${m[1]}`
  }
  m = str.match(/^[a-z]+$/i)
  if (m) {
    return string as string
  }
  return undefined
}

export function interpolateColor(color2: string, color1: string, weight: number) {
  const w = weight * 2 - 1
  const w1 = (w / 1 + 1) / 2
  const w2 = 1 - w1
  const [r1, g1, b1] = getRGBFromHex(color1)
  const [r2, g2, b2] = getRGBFromHex(color2)
  const components = [
    Math.round(r1 * w1 + r2 * w2),
    Math.round(g1 * w1 + g2 * w2),
    Math.round(b1 * w1 + b2 * w2),
  ] as const
  return rgbToHex(components)
}

export function getColorFromGradient(gradientData: Array<[string, number]>, percent: number) {
  const data = gradientData.slice().sort((a, b) => a[1] - b[1]) // sort by weight ascending
  for (let i = 0; i < data.length; i++) {
    const [color, weight2] = data[i]!
    if (percent <= weight2) {
      if (i === 0) return color
      const [prevColor, weight1] = data[i - 1]!
      const weight = (percent - weight1) / (weight2 - weight1)
      return interpolateColor(prevColor, color, weight)
    }
  }
  return data[data.length - 1]![0]
}
